import * as grpc from "grpc"; // these are purely just types
import * as grpcWeb from "grpc-web";
import { Observable } from "rxjs";
import { Message } from "google-protobuf";
import { bufferTime } from "rxjs/operators";

function fromStream<Response extends Message, Request extends Message>(
  stream:
    | grpc.ClientReadableStream<Response>
    | grpc.ClientDuplexStream<Request, Response>,
  completeOnEof: boolean = true,
  request?: Request
) {
  return new Observable<Response>(obs => {
    stream.on("data", response => {
      obs.next(response);
    });
    if (completeOnEof) {
      stream.on("status", status => {
        if (status.code === 0) {
          obs.complete();
        }
      });
    }

    stream.on("end", () => {
      obs.complete();
    });

    if ("write" in stream && request) {
      stream.write(request);
    }

    stream.on("error", error => {
      obs.error(error);
    });

    return function cleanup() {
      if (stream && stream.cancel) {
        stream.cancel();
      }
    };
  });
}

export function fromDuplexStream<
  Response extends Message,
  Request extends Message
>(
  stream: grpc.ClientDuplexStream<Request, Response>,
  completeOnEof: boolean = true,
  request?: Request
) {
  return fromStream(stream, completeOnEof, request);
}

export function fromReadableStream<Response extends Message>(
  stream:
    | grpc.ClientReadableStream<Response>
    | grpcWeb.ClientReadableStream<Response>,
  completeOnEof: boolean = true
) {
  return fromStream(
    stream as grpc.ClientReadableStream<Response>,
    completeOnEof
  );
}

type requestCallback<R> = (error: any, response?: R) => void;

export function fromUnary<Response extends Message>(
  streamFn: (callback: requestCallback<Response>) => { cancel?(): void }
) {
  return new Observable<Response>(obs => {
    const callback: requestCallback<Response> = (error, response) => {
      if (error) {
        obs.error(error);
        return;
      }
      obs.next(response);
      obs.complete();
    };

    const stream = streamFn(callback);

    return function cleanup() {
      if (stream && stream.cancel) {
        stream.cancel();
      }
    };
  });
}

export const grpcDataBuffer = <T>() => bufferTime<T>(240);
